<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, ContentType, Tag } from '@/models/content'
import { isFallbackImage } from '@/utils/content'

const props = defineProps({
  contentType: {
    type: String as PropType<ContentType>,
    default: 'article',
    validator(value: string) {
      return ['article', 'chart', 'video'].includes(value)
    },
  },
  slug: {
    type: [String, Number],
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  excerpt: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  pillsLimit: {
    type: Number,
    default: 2,
  },
  coverUrl: {
    type: String,
    required: true,
  },
  scaleCover: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String as PropType<'primary' | 'secondary'>,
    default: 'primary',
  },
  verticallyOrientation: {
    type: Boolean,
    default: false,
  },
})

const limitedPills = computed(() => props.pills.slice(0, props.pillsLimit))

const formattedDuration = useFormattedDuration(
  props.contentType,
  props.duration,
)

const href = useContentHref(props.contentType, props.slug)

const coverClasses = computed(() => {
  if (isFallbackImage(props.coverUrl)) {
    return 'object-center'
  } else {
    return props.scaleCover ? 'object-center' : 'object-top'
  }
})
</script>

<template>
  <BaseLink
    :href="href"
    as="a"
    variant="unstyled"
    class="card-hover-lg"
    data-testid="featured-category-card"
  >
    <article
      class="lg:flex"
      :class="{
        'bg-s-900': variant === 'primary',
        'bg-warm-gray': variant === 'secondary',
        'flex-col lg:h-[638px]': verticallyOrientation,
        'lg:h-[480px]': !verticallyOrientation,
      }"
    >
      <div
        class="relative h-full pb-9/16 lg:pb-0"
        :class="{ 'lg:w-2/3': !verticallyOrientation }"
      >
        <img
          :src="$cloudinary.resize(coverUrl, 'featured-category-thumbnail')"
          class="absolute h-full w-full object-cover lg:relative"
          :class="coverClasses"
          alt=""
          width="500"
          height="500"
        />
        <div
          class="absolute top-0 hidden h-full w-full border-2 border-black/10 lg:block"
        />
      </div>

      <div class="p-5 lg:p-8" :class="{ 'lg:w-1/3': !verticallyOrientation }">
        <p class="text-xs font-semibold text-s-500">
          {{ publishedDate }} · {{ formattedDuration }}
        </p>
        <h1
          class="mt-2 line-clamp-6 text-lg font-semibold leading-snug md:font-bold"
          :class="[
            {
              'text-white': variant === 'primary',
              'text-black': variant === 'secondary',
            },
            verticallyOrientation ? 'md:text-xl' : 'md:text-3xl',
          ]"
          :title="title"
          data-testid="featured-category-title"
        >
          {{ title }}
        </h1>
        <div
          class="custom-line-clamp mt-3 text-base md:mt-4"
          :class="[
            {
              'text-white': variant === 'primary',
              'text-black': variant === 'secondary',
            },
            verticallyOrientation ? 'sm:text-sm' : 'sm:text-base',
          ]"
          v-html="excerpt"
        />
        <div class="mt-4 flex flex-wrap gap-2">
          <LinkedPill
            v-for="pill in limitedPills"
            :key="pill.id"
            :type="pill.__typename"
            :slug="pill.slug"
            :variant="variant === 'primary' ? 'yellow' : 'black'"
          >
            {{ pill.name }}
          </LinkedPill>
        </div>
      </div>
    </article>
  </BaseLink>
</template>
